import type { ClientMessage, ServerMessage } from '@repo/shared/protocol';

export type SignalingStatus = 'connecting' | 'open' | 'closed';

/** Close-Codes vom Server, nach denen nicht automatisch neu verbunden wird (siehe Gateway). */
export const FATAL_CLOSE_CODES: ReadonlySet<number> = new Set([4003, 4008, 4029]);

export interface SignalingOptions {
  url?: string;
  onMessage: (msg: ServerMessage) => void;
  onStatus: (status: SignalingStatus, closeCode?: number) => void;
}

/**
 * WebSocket zum Signaling-Server mit automatischem Wiederverbinden (exponentielles Backoff).
 * Ohne Verbindung gesendete Nachrichten verfallen bewusst – nach dem Wiederverbinden stellt
 * die ChatSession ihren Zustand selbst wieder her.
 */
export class SignalingClient {
  readonly #url: string;
  readonly #options: SignalingOptions;
  #ws: WebSocket | null = null;
  #attempt = 0;
  #retryTimer: number | undefined;
  #stopped = true;

  constructor(options: SignalingOptions) {
    this.#options = options;
    this.#url = options.url ?? defaultUrl();
  }

  connect(): void {
    if (!this.#stopped) return;
    this.#stopped = false;
    this.#open();
  }

  send(msg: ClientMessage): boolean {
    if (this.#ws?.readyState !== WebSocket.OPEN) return false;
    this.#ws.send(JSON.stringify(msg));
    return true;
  }

  close(): void {
    this.#stopped = true;
    window.clearTimeout(this.#retryTimer);
    const ws = this.#ws;
    this.#ws = null;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = null;
      ws.close(1000);
    }
    this.#options.onStatus('closed');
  }

  #open(): void {
    this.#options.onStatus('connecting');
    const ws = new WebSocket(this.#url);
    this.#ws = ws;

    ws.onopen = () => {
      this.#attempt = 0;
      this.#options.onStatus('open');
    };
    ws.onmessage = (event: MessageEvent) => {
      if (typeof event.data !== 'string') return;
      try {
        this.#options.onMessage(JSON.parse(event.data) as ServerMessage);
      } catch (error) {
        console.warn('Signaling-Nachricht nicht verarbeitet', error);
      }
    };
    ws.onclose = (event) => {
      if (this.#ws !== ws) return;
      this.#ws = null;
      this.#options.onStatus('closed', event.code);
      if (this.#stopped || FATAL_CLOSE_CODES.has(event.code)) return;
      const delay = Math.min(8_000, 500 * 2 ** this.#attempt) * (0.75 + Math.random() * 0.5);
      this.#attempt += 1;
      this.#retryTimer = window.setTimeout(() => this.#open(), delay);
    };
  }
}

function defaultUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}
